import { mdiClockOutline, mdiCommentText, mdiContentSave, mdiInformationOutline } from '@mdi/js'
import Icon from '@mdi/react'
import FavIcon from '@mui/icons-material/Favorite'
import RetweetIcon from '@mui/icons-material/Repeat'
import ShareIcon from '@mui/icons-material/Reply'
import LikeIcon from '@mui/icons-material/ThumbUp'
import Tooltip from '@mui/material/Tooltip'
import { format } from 'date-fns'
import { Fragment, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { PostDestinationType } from 'shared/types'
import styles from './PostedActions.pcss'
import { BRAND_FACEBOOK, BRAND_INSTAGRAM, BRAND_LINKEDIN, BRAND_PINTEREST, BRAND_TWITTER } from 'shared'

interface PostedStatsProps {
  likes: string
  shares: string
  comments: string
  saves?: string
  isRepin?: boolean
  network: PostDestinationType
  lastUpdated?: string // "2026-02-10T10:31:50+00:00"
}

const NETWORKS_WITH_SHARES = [BRAND_FACEBOOK, BRAND_PINTEREST]
const NETWORKS_WITH_COMMENTS = [BRAND_FACEBOOK, BRAND_LINKEDIN, BRAND_INSTAGRAM, BRAND_PINTEREST]

export function PostedStats(props: PostedStatsProps) {
  const showShares = NETWORKS_WITH_SHARES.includes(props.network)
  const showComments = NETWORKS_WITH_COMMENTS.includes(props.network)
  const isPinterest = props.network === BRAND_PINTEREST
  const showPinterestStats = isPinterest && !props.isRepin

  const likeIcon = useMemo(() => {
    if (props.network === BRAND_INSTAGRAM) {
      return <FavIcon />
    }
    if (props.network === BRAND_TWITTER) {
      return null
    }
    return <LikeIcon />
  }, [props.network])

  if (props.isRepin) {
    return (
      <div className={styles.stats}>
        <div className={styles['pi-message']}>
          <Icon path={mdiInformationOutline} className={styles['mdi-icon']} />
          <FormattedMessage id="post.labels.no-repin-stats" />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.stats}>
      <div className={styles['stats-row']}>
        <Tooltip title={<FormattedMessage id="post.posted.stats.comments" />}>
          <span key="comments" className={`${styles['stats-box']} ${showComments ? '' : styles.hidden}`}>
            <Icon path={mdiCommentText} className={styles['mdi-icon']} />
            <span className={styles.numbers}>{props.comments}</span>
          </span>
        </Tooltip>
        <Tooltip title={<FormattedMessage id="post.posted.stats.likes" />}>
          <span key="likes" className={`${styles['stats-box']} ${!likeIcon ? styles.hidden : ''}`}>
            {likeIcon}
            <span className={styles.numbers}>{props.likes}</span>
          </span>
        </Tooltip>
        <Tooltip title={<FormattedMessage id={`post.posted.stats.${props.network === BRAND_TWITTER ? 'retweets' : 'shares'}`} />}>
          <span key="shares" className={`${styles['stats-box']} ${showShares ? '' : styles.hidden}`}>
            {props.network === BRAND_TWITTER ? <RetweetIcon /> : <ShareIcon className={styles['icon-share']} />}
            <span className={styles.numbers}>{props.shares}</span>
          </span>
        </Tooltip>
        {isPinterest && (
          <Fragment>
            <Tooltip title={<FormattedMessage id="post.posted.stats.saves" />}>
              <span key="saves" className={`${styles['stats-box']} ${showPinterestStats ? '' : styles.hidden}`}>
                <Icon path={mdiContentSave} className={styles['mdi-icon']} />
                <span className={styles.numbers}>{props.saves}</span>
              </span>
            </Tooltip>
          </Fragment>
        )}
      </div>
      {props.lastUpdated && (
        <Tooltip title={`Last updated on ${format(new Date(props.lastUpdated), 'MMM d, h:mm a')}`}>
          <div className={styles['last-updated']}>
            <Icon path={mdiClockOutline} className={styles['mdi-icon']} />
            {format(new Date(props.lastUpdated), 'MMM d, h:mm a')}
          </div>
        </Tooltip>
      )}
    </div>
  )
}

export default PostedStats
